import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useOrders } from '@/lib/queries';
import { ChefNav } from '@/components/ChefNav';
import { useNotifications } from '@/lib/queries';
import { StatusBadge, orderStatusTone, humaniseStatus } from '@/components/ui/badge';
import { formatDateTime } from '@/lib/utils';

// Order History — log of all purchase orders for the chef's location(s).
export function OrderHistory() {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number.parseInt(searchParams.get('page') ?? '1', 10) || 1;
  const { data, isLoading } = useOrders(page);
  const { data: notifications } = useNotifications();
  const [keyword, setKeyword] = useState('');

  const orders = data?.data ?? [];
  const filtered = keyword
    ? orders.filter((o) => o.id.toLowerCase().includes(keyword.toLowerCase()))
    : orders;

  return (
    <div className="min-h-screen bg-canvas">
      <ChefNav notifications={notifications?.data ?? []} />
      <div className="mx-auto max-w-7xl px-4 py-6 md:px-6">
        <h1 className="page-title">Order History</h1>
        <div className="mb-6 mt-4 flex max-w-sm">
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="Filter by order #"
            className="h-10 w-full rounded-card border border-line bg-surface-2 px-3 text-sm text-ink placeholder:text-faint focus:border-brand focus:outline-none"
          />
        </div>

        <div className="card overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-line text-muted">
                <th className="px-5 py-3 font-medium">Order #</th>
                <th className="px-5 py-3 font-medium">Date Placed</th>
                <th className="px-5 py-3 font-medium">Location</th>
                <th className="px-5 py-3 font-medium">Items</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={6} className="px-5 py-8 text-center text-muted">
                    Loading…
                  </td>
                </tr>
              )}
              {!isLoading && filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-5 py-8 text-center text-muted">
                    No orders yet.
                  </td>
                </tr>
              )}
              {filtered.map((o) => (
                <tr key={o.id} className="border-b border-line hover:bg-surface-2">
                  <td className="px-5 py-3 font-mono text-brand">{o.orderNumber}</td>
                  <td className="px-5 py-3 text-ink">{formatDateTime(o.submittedAt)}</td>
                  <td className="px-5 py-3 text-ink">{o.locationName}</td>
                  <td className="px-5 py-3 text-ink">{o.itemCount}</td>
                  <td className="px-5 py-3">
                    <StatusBadge tone={orderStatusTone(o.status)}>{humaniseStatus(o.status)}</StatusBadge>
                  </td>
                  <td className="px-5 py-3">
                    <Link to={`/app/orders/${o.id}`} className="text-brand hover:underline">
                      View
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {data && data.pagination.totalPages > 1 && (
          <div className="mt-4 flex items-center gap-2">
            <button
              className="rounded px-3 py-1.5 text-sm text-muted hover:bg-surface-2 hover:text-ink disabled:opacity-40"
              disabled={page <= 1}
              onClick={() => setSearchParams({ page: String(page - 1) })}
            >
              Previous
            </button>
            <span className="text-sm text-muted">
              Page {page} of {data.pagination.totalPages}
            </span>
            <button
              className="rounded px-3 py-1.5 text-sm text-muted hover:bg-surface-2 hover:text-ink disabled:opacity-40"
              disabled={page >= data.pagination.totalPages}
              onClick={() => setSearchParams({ page: String(page + 1) })}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
